// Cookie consent utility functions

/**
 * Get the current cookie consent settings
 * @returns {Object|null} Cookie consent object or null if not set
 */
export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem('cookieConsent');
    return consent ? JSON.parse(consent) : null;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
};

/**
 * Check if a specific cookie category is allowed
 * @param {string} category - Cookie category ('necessary', 'analytics', 'marketing')
 * @returns {boolean} True if allowed, false otherwise
 */
export const isCookieAllowed = (category) => {
  const consent = getCookieConsent();
  if (!consent) {
    // If no consent is given, only necessary cookies are allowed
    return category === 'necessary';
  }
  return Boolean(consent[category]);
};

/**
 * Check if user has made any cookie choice
 * @returns {boolean} True if user has made a choice, false otherwise
 */
export const hasUserMadeCookieChoice = () => {
  return getCookieConsent() !== null;
};

/**
 * Save cookie consent settings
 * @param {Object} settings - Cookie settings object
 */
export const saveCookieConsent = (settings) => {
  try {
    const consent = {
      ...settings,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    
    // Dispatch custom event for other components to listen to
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
      detail: consent 
    }));
    
    return consent;
  } catch (error) {
    console.error('Error saving cookie consent:', error);
    return null;
  }
};

/**
 * Clear cookie consent (for testing purposes)
 */
export const clearCookieConsent = () => {
  try {
    localStorage.removeItem('cookieConsent');
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
      detail: null 
    }));
  } catch (error) {
    console.error('Error clearing cookie consent:', error);
  }
};

/**
 * Initialize analytics tools based on consent
 */
export const initializeAnalytics = () => {
  if (isCookieAllowed('analytics')) {
    // Initialize Google Analytics, etc.
    console.log('Analytics initialized');
    // Example: gtag('config', 'GA_MEASUREMENT_ID');
  }
};

/**
 * Initialize marketing tools based on consent
 */
export const initializeMarketing = () => {
  if (isCookieAllowed('marketing')) {
    // Initialize marketing pixels, etc.
    console.log('Marketing tools initialized');
    // Example: Facebook Pixel, Google Ads, etc.
  }
};

/**
 * Initialize all tracking based on current consent
 */
export const initializeTracking = () => {
  initializeAnalytics();
  initializeMarketing();
};

// Default cookie settings
export const DEFAULT_COOKIE_SETTINGS = {
  necessary: true,
  analytics: false,
  marketing: false
};